import { useState, useEffect } from 'react';
import { Users, TrendingUp, ChevronDown, ChevronUp } from 'lucide-react';
import { apiService } from '../services/api';
import { Loading, ErrorMessage, Card } from '../components/SharedComponents';

function Teams() {
  const [teams, setTeams] = useState([]);
  const [squads, setSquads] = useState(null);
  const [teamStats, setTeamStats] = useState({});
  const [expandedTeam, setExpandedTeam] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchTeams();
  }, []);

  const fetchTeams = async () => {
    try {
      setLoading(true);
      setError(null);
      try {
        const datasetResponse = await apiService.getDatasetTeams();
        if (datasetResponse.data && datasetResponse.data.length > 0) {
          setTeams(datasetResponse.data);
        } else {
          const response = await apiService.getTeams({ limit: 100 });
          setTeams(response.data);
        }
      } catch (err) {
        const response = await apiService.getTeams({ limit: 100 });
        setTeams(response.data);
      }

      try {
        const squadsResponse = await apiService.getSquads();
        setSquads(squadsResponse.data);
      } catch (err) {
        console.log('Squads not available:', err);
      }

      try {
        const statTypes = ['total_runs', 'total_wickets', 'match_win', 'highest_score', 'lowest_score'];
        const statsPromises = statTypes.map(type =>
          apiService.getTeamStats(type).catch(() => null)
        );
        const statsResults = await Promise.all(statsPromises);
        const statsMap = {};
        statsResults.forEach((result, idx) => {
          if (result && result.data) {
            statsMap[statTypes[idx]] = result.data;
          }
        });
        setTeamStats(statsMap);
      } catch (err) {
        console.log('Team stats not available:', err);
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const getTeamSquad = (teamId) => {
    if (!squads || !Array.isArray(squads)) return [];
    const teamSquad = squads.find(squad =>
      (squad.team && (squad.team.tid === teamId || squad.team.id === teamId)) ||
      squad.team_id === teamId
    );
    return teamSquad?.players || teamSquad?.squad || [];
  };

  const getTeamStat = (teamId, statType) => {
    const stats = teamStats[statType];
    if (!stats || !Array.isArray(stats)) return null;
    return stats.find(stat => {
      const statTeamId = stat.team?.tid || stat.team?.id || stat.team_id;
      return statTeamId === teamId;
    });
  };

  if (loading) return <Loading message="Loading teams..." />;
  if (error) return <ErrorMessage message={error} onRetry={fetchTeams} />;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '12px' }}>
        <div>
          <h1 style={{
            fontFamily: "'Space Grotesk', sans-serif",
            fontSize: '32px',
            fontWeight: '700',
            color: '#1c1917',
            letterSpacing: '-0.5px'
          }}>IPL Teams</h1>
          <p style={{ color: '#57534e', marginTop: '6px', fontSize: '15px' }}>Browse all IPL teams and squads</p>
        </div>
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          padding: '8px 14px',
          backgroundColor: '#fafaf9',
          borderRadius: '10px',
          border: '1px solid #f5f5f4'
        }}>
          <Users style={{ width: '18px', height: '18px', color: '#e85d04' }} />
          <span style={{ fontWeight: '600', fontSize: '14px', color: '#1c1917' }}>{teams.length} Teams</span>
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))', gap: '20px' }}>
        {teams.map((team) => {
          const teamId = team.tid || team.id;
          const teamName = team.title || team.name;
          const teamAbbr = team.abbr || team.shortName;
          const logoUrl = team.logo_url || team.thumb_url;
          const isExpanded = expandedTeam === teamId;
          const squad = getTeamSquad(teamId);
          const totalRuns = getTeamStat(teamId, 'total_runs');
          const totalWickets = getTeamStat(teamId, 'total_wickets');
          const matchWins = getTeamStat(teamId, 'match_win');
          const highestScore = getTeamStat(teamId, 'highest_score');

          return (
            <Card key={teamId}>
              <div style={{ textAlign: 'center' }}>
                {logoUrl ? (
                  <div style={{ margin: '0 auto 16px', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                    <img
                      src={logoUrl}
                      alt={teamName}
                      style={{
                        width: '72px',
                        height: '72px',
                        objectFit: 'contain',
                        borderRadius: '12px'
                      }}
                      onError={(e) => {
                        e.target.style.display = 'none';
                        const fallback = e.target.nextSibling;
                        if (fallback) fallback.style.display = 'flex';
                      }}
                    />
                    <div style={{
                      width: '72px',
                      height: '72px',
                      background: 'linear-gradient(135deg, #e85d04 0%, #ff7b29 100%)',
                      borderRadius: '16px',
                      display: 'none',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}>
                      <span style={{ fontSize: '24px', fontWeight: '700', color: 'white' }}>{teamAbbr}</span>
                    </div>
                  </div>
                ) : (
                  <div style={{
                    width: '72px',
                    height: '72px',
                    background: 'linear-gradient(135deg, #e85d04 0%, #ff7b29 100%)',
                    borderRadius: '16px',
                    margin: '0 auto 16px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}>
                    <span style={{ fontSize: '24px', fontWeight: '700', color: 'white' }}>{teamAbbr}</span>
                  </div>
                )}
                <h3 style={{
                  fontFamily: "'Space Grotesk', sans-serif",
                  fontSize: '18px',
                  fontWeight: '600',
                  color: '#1c1917',
                  marginBottom: '8px'
                }}>{teamName}</h3>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', fontSize: '13px', color: '#57534e', marginBottom: '16px' }}>
                  <p><span style={{ fontWeight: '600', color: '#1c1917' }}>Abbreviation:</span> {teamAbbr || 'N/A'}</p>
                  <p><span style={{ fontWeight: '600', color: '#1c1917' }}>Country:</span> {team.country || team.country_iso || 'N/A'}</p>
                  {team.city && <p><span style={{ fontWeight: '600', color: '#1c1917' }}>City:</span> {team.city}</p>}
                  {team.homeGround && <p><span style={{ fontWeight: '600', color: '#1c1917' }}>Home:</span> {team.homeGround}</p>}
                  {team.captain && <p><span style={{ fontWeight: '600', color: '#1c1917' }}>Captain:</span> {team.captain}</p>}
                </div>

                {(totalRuns || totalWickets || matchWins || highestScore) && (
                  <div style={{ marginBottom: '16px', padding: '14px', backgroundColor: '#fafaf9', borderRadius: '12px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '10px' }}>
                      <TrendingUp style={{ width: '16px', height: '16px', color: '#e85d04' }} />
                      <span style={{ fontSize: '13px', fontWeight: '600', color: '#1c1917' }}>Season Stats</span>
                    </div>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '10px', fontSize: '12px' }}>
                      {totalRuns && (
                        <div style={{ textAlign: 'left' }}>
                          <span style={{ color: '#57534e' }}>Total Runs</span>
                          <div style={{ fontWeight: '700', color: '#1c1917', fontSize: '14px' }}>
                            {totalRuns.total_runs || totalRuns.runs || 'N/A'}
                          </div>
                        </div>
                      )}
                      {totalWickets && (
                        <div style={{ textAlign: 'left' }}>
                          <span style={{ color: '#57534e' }}>Total Wickets</span>
                          <div style={{ fontWeight: '700', color: '#1c1917', fontSize: '14px' }}>
                            {totalWickets.total_wickets || totalWickets.wickets || 'N/A'}
                          </div>
                        </div>
                      )}
                      {matchWins && (
                        <div style={{ textAlign: 'left' }}>
                          <span style={{ color: '#57534e' }}>Wins</span>
                          <div style={{ fontWeight: '700', color: '#22c55e', fontSize: '14px' }}>
                            {matchWins.wins || matchWins.total_wins || 'N/A'}
                          </div>
                        </div>
                      )}
                      {highestScore && (
                        <div style={{ textAlign: 'left' }}>
                          <span style={{ color: '#57534e' }}>Highest Score</span>
                          <div style={{ fontWeight: '700', color: '#1c1917', fontSize: '14px' }}>
                            {highestScore.score || highestScore.highest_score || 'N/A'}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {squad.length > 0 && (
                  <div>
                    <button
                      onClick={() => setExpandedTeam(isExpanded ? null : teamId)}
                      style={{
                        width: '100%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        padding: '12px 14px',
                        backgroundColor: '#fafaf9',
                        border: '1px solid #f5f5f4',
                        borderRadius: '10px',
                        cursor: 'pointer',
                        fontSize: '13px',
                        fontWeight: '600',
                        color: '#1c1917',
                        transition: 'all 0.2s ease'
                      }}
                      onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f5f5f4'}
                      onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#fafaf9'}
                    >
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <Users style={{ width: '16px', height: '16px', color: '#e85d04' }} />
                        <span>Squad ({squad.length} players)</span>
                      </div>
                      {isExpanded ? <ChevronUp style={{ width: '16px', height: '16px' }} /> : <ChevronDown style={{ width: '16px', height: '16px' }} />}
                    </button>
                    {isExpanded && (
                      <div style={{ marginTop: '12px', maxHeight: '280px', overflowY: 'auto' }}>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                          {squad.map((player, idx) => (
                            <div
                              key={player.pid || player.id || idx}
                              style={{
                                padding: '10px 12px',
                                backgroundColor: '#fafaf9',
                                borderRadius: '8px',
                                fontSize: '13px',
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                                border: '1px solid #f5f5f4'
                              }}
                            >
                              <span style={{ fontWeight: '500', color: '#1c1917' }}>
                                {player.title || player.name || 'Unknown'}
                              </span>
                              {player.role && (
                                <span style={{
                                  padding: '3px 8px',
                                  background: 'linear-gradient(135deg, #6366f1 0%, #818cf8 100%)',
                                  color: 'white',
                                  borderRadius: '6px',
                                  fontSize: '10px',
                                  fontWeight: '600',
                                  textTransform: 'uppercase'
                                }}>
                                  {player.role}
                                </span>
                              )}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </Card>
          );
        })}
      </div>
    </div>
  );
}

export default Teams;
